import React, { useEffect, useRef, useState, useCallback } from 'react';
import { FlowPreviewEngine, MAX_STEPS } from './FlowPreviewEngine';
import './FlowPreviewPanel.css';

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Normalise node_type aliases — mirrors FlowBuilder's normalizeNodeType */
const normalizeType = (raw = '') => {
  const MAP = {
    ask_input: 'input', ask_for_input: 'input', collect_input: 'input',
    ai: 'ai_handoff', handoff: 'ai_handoff',
    stop: 'end', finish: 'end', complete: 'end',
  };
  const t = String(raw || '').toLowerCase().trim();
  return MAP[t] || t || 'message';
};

const truncate = (text = '', max = 60) =>
  String(text || '').length > max ? String(text).slice(0, max) + '…' : String(text || '');

/**
 * Substitute {{variable}} tokens inside api_call content fields
 * so the real HTTP request uses collected values.
 */
const substituteApiCallContent = (engine, content, vars) => {
  const sub = (v) => {
    if (typeof v === 'string') return engine.substituteVariables(v, vars);
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      return Object.fromEntries(Object.entries(v).map(([k, val]) => [k, sub(val)]));
    }
    return v;
  };
  return {
    ...content,
    url: engine.substituteVariables(content.url || '', vars),
    headers: sub(content.headers || {}),
    body: content.body ? sub(content.body) : undefined,
  };
};

/** Resolve company name from sessionStorage */
const getCompanyName = () => {
  try {
    const u = JSON.parse(sessionStorage.getItem('user') || '{}');
    return u?.name || 'WhatsApp Business';
  } catch {
    return 'WhatsApp Business';
  }
};

// ─── ChatBubble ───────────────────────────────────────────────────────────────

const TYPE_LABEL = {
  message: 'Message', input: 'Input', ai_handoff: 'AI Handoff',
  api_call: 'API Call', end: 'End',
};

// Reply arrow SVG used in button options
const ReplyArrowIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <polyline points="9 14 4 9 9 4"/>
    <path d="M20 20v-7a4 4 0 0 0-4-4H4"/>
  </svg>
);

const DoubleTick = () => (
  <svg width="14" height="11" viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: 2 }}>
    <path d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.879a.32.32 0 0 1-.484.033l-.358-.325a.319.319 0 0 0-.484.033l-.378.387a.32.32 0 0 0 .033.484l1.025.93a.32.32 0 0 0 .484-.033l6.272-8.048a.366.366 0 0 0-.063-.51zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.879a.32.32 0 0 1-.484.033L3.724 9.587a.319.319 0 0 0-.484.033l-.378.387a.32.32 0 0 0 .033.484l1.025.93a.32.32 0 0 0 .484-.033L10.676 3.84a.366.366 0 0 0-.063-.51z" fill="#4fc3f7"/>
  </svg>
);

const ChatBubble = React.memo(({ item, isLastWithChoices, onChoice }) => {
  const timeStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  switch (item.type) {
    case 'bot': {
      const hasOpts = Array.isArray(item.options) && item.options.length > 0;
      return (
        <div className="fpp-bubble-row fpp-bubble-row--bot">
          <div className={`fpp-bubble-bot${hasOpts ? ' fpp-bubble-bot--has-options' : ''}`}>
            {item.nodeType && item.nodeType !== 'message' && (
              <span className="fpp-node-tag">{TYPE_LABEL[item.nodeType] || item.nodeType}</span>
            )}
            <div className="fpp-bubble-content-row">
              <span className="fpp-bubble-text">{item.text}</span>
              <span className="fpp-bubble-time fpp-bubble-time--inline">{timeStr}</span>
            </div>
            {hasOpts && (
              <div className="fpp-bubble-options">
                {item.options.map((opt) => (
                  <button
                    key={opt.id}
                    className={`fpp-bubble-opt-btn${isLastWithChoices ? '' : ' fpp-bubble-opt-btn--used'}`}
                    onClick={isLastWithChoices ? () => onChoice(opt.id, opt.label, item.text) : undefined}
                    disabled={!isLastWithChoices}
                  >
                    <ReplyArrowIcon />
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      );
    }

    case 'user':
      return (
        <div className="fpp-bubble-row fpp-bubble-row--user">
          <div className="fpp-bubble-user">
            {item.quotedText && (
              <div className="fpp-bubble-quote">
                <span className="fpp-bubble-quote-from">{item.quotedFrom || 'Bot'}</span>
                <span className="fpp-bubble-quote-text">{truncate(item.quotedText, 80)}</span>
              </div>
            )}
            <div className="fpp-bubble-content-row">
              <span className="fpp-bubble-text">{item.text}</span>
              <span className="fpp-bubble-time fpp-bubble-time--sent fpp-bubble-time--inline">
                {timeStr}<DoubleTick />
              </span>
            </div>
          </div>
        </div>
      );

    case 'api_loading':
      return (
        <div className="fpp-bubble-row fpp-bubble-row--system">
          <div className="fpp-bubble-api fpp-bubble-api--loading">
            <span className="fpp-spinner" />
            <span>Calling <strong>{item.method}</strong> {truncate(item.url, 44)}…</span>
          </div>
        </div>
      );

    case 'api_result': {
      const hasVars = Object.keys(item.extractedVars || {}).length > 0;
      return (
        <div className="fpp-bubble-row fpp-bubble-row--system">
          <div className={`fpp-bubble-api${item.success ? ' fpp-bubble-api--ok' : ' fpp-bubble-api--err'}`}>
            <div className="fpp-bubble-api-label">
              {item.success ? '✓' : '✗'} {item.method} {truncate(item.url, 36)}
            </div>
            {!item.success && item.errorMessage && (
              <div className="fpp-bubble-api-detail">{item.errorMessage}</div>
            )}
            {item.success && hasVars && (
              <div className="fpp-bubble-api-vars">
                {Object.entries(item.extractedVars).map(([k, v]) => (
                  <div key={k}><strong>{k}</strong>: {String(v ?? '(empty)')}</div>
                ))}
              </div>
            )}
          </div>
        </div>
      );
    }

    case 'system':
      return (
        <div className="fpp-bubble-row fpp-bubble-row--center">
          <div className={`fpp-bubble-system fpp-bubble-system--${item.severity || 'info'}`}>
            {item.text}
          </div>
        </div>
      );

    default:
      return null;
  }
});

// ─── FlowPreviewPanel ─────────────────────────────────────────────────────────

/**
 * FlowPreviewPanel
 *
 * Renders the flow preview as a sidebar panel (not a modal overlay).
 * All engine logic is identical to FlowPreviewModal.
 *
 * Props:
 *  onClose           {function}  — called when user exits preview
 *  nodes             {Array}     — ReactFlow nodes from FlowBuilder state
 *  edges             {Array}     — ReactFlow edges from FlowBuilder state
 *  hasDraftChanges   {boolean}   — true if draftContent differs from saved state
 *  onPreviewApiCall  {function}  — async (body) → axios response
 *  validateFlow      {function}  — (nodes, edges) → { errors, warnings }
 */
const FlowPreviewPanel = ({
  onClose,
  nodes = [],
  edges = [],
  hasDraftChanges = false,
  onPreviewApiCall,
  validateFlow,
}) => {
  const companyName = getCompanyName();
  const hasApiCallNodes = nodes.some((n) => normalizeType(n.data?.node_type) === 'api_call');

  // ── Validation state ──────────────────────────────────────────────────
  const [validation, setValidation] = useState({ errors: [], warnings: [] });
  const [warningsDismissed, setWarningsDismissed] = useState(false);
  const [apiDisclaimerAcknowledged, setApiDisclaimerAcknowledged] = useState(false);

  // ── Preview session state ─────────────────────────────────────────────
  const [phase, setPhase] = useState('idle'); // 'idle' | 'running' | 'terminal'
  const [history, setHistory] = useState([]);
  const [variables, setVariables] = useState({});
  const [waitingFor, setWaitingFor] = useState(null); // null | 'user_choice' | 'user_input'
  const [inputText, setInputText] = useState('');
  const [currentNodeId, setCurrentNodeId] = useState(null);
  const [path, setPath] = useState([]);
  const [stepCount, setStepCount] = useState(0);

  // ── Refs ──────────────────────────────────────────────────────────────
  const engineRef = useRef(null);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  const stepCountRef = useRef(0);
  const variablesRef = useRef({});

  // ── Auto-scroll chat ──────────────────────────────────────────────────
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history]);

  // ── Run validation whenever nodes/edges change ────────────────────────
  useEffect(() => {
    if (typeof validateFlow === 'function') {
      setValidation(validateFlow(nodes, edges));
    }
  }, [nodes, edges, validateFlow]);

  // ── Reset everything on mount ─────────────────────────────────────────
  useEffect(() => {
    setPhase('idle');
    setHistory([]);
    setVariables({});
    setWaitingFor(null);
    setInputText('');
    setCurrentNodeId(null);
    setPath([]);
    setStepCount(0);
    setWarningsDismissed(false);
    setApiDisclaimerAcknowledged(false);
    stepCountRef.current = 0;
    variablesRef.current = {};
    engineRef.current = null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pushHistory = useCallback((item) => {
    setHistory((prev) => [...prev, item]);
  }, []);

  // ── Main traversal function ───────────────────────────────────────────
  const stepEngine = useCallback(async (engine, nodeId, vars) => {
    if (!nodeId) {
      pushHistory({ type: 'system', text: 'Flow ended — no next node.', severity: 'info' });
      setWaitingFor(null);
      setPhase('terminal');
      return;
    }

    if (stepCountRef.current >= MAX_STEPS) {
      pushHistory({
        type: 'system',
        text: `⚠ Maximum step limit (${MAX_STEPS}) reached — possible infinite loop. Preview stopped.`,
        severity: 'error',
      });
      setWaitingFor(null);
      setPhase('terminal');
      return;
    }
    stepCountRef.current += 1;
    setStepCount(stepCountRef.current);

    const node = engine.getNode(nodeId);
    if (!node) {
      pushHistory({ type: 'system', text: `Node "${nodeId}" not found.`, severity: 'error' });
      setWaitingFor(null);
      setPhase('terminal');
      return;
    }

    const type = normalizeType(node.data?.node_type);
    const content = node.data?.content || {};
    setCurrentNodeId(nodeId);
    setPath((prev) => [...prev, nodeId]);

    // ── message ───────────────────────────────────────────────────────
    if (type === 'message') {
      const text = engine.substituteVariables(content.text || '', vars);
      const options = Array.isArray(content.options) ? content.options : [];
      const outgoing = engine.getOutgoing(nodeId);

      pushHistory({ type: 'bot', text, nodeType: type, nodeId, options });

      if (options.length > 0) {
        setWaitingFor('user_choice');
        return;
      }

      const next = engine.resolveNext(nodeId, 'default');
      if (!next && outgoing.length === 0) {
        pushHistory({ type: 'system', text: 'Dead end — no outgoing edges.', severity: 'warning' });
        setPhase('terminal');
        return;
      }
      await stepEngine(engine, next, vars);
      return;
    }

    // ── input ──────────────────────────────────────────────────────────
    if (type === 'input') {
      const text = engine.substituteVariables(content.text || '', vars);
      pushHistory({ type: 'bot', text, nodeType: type, nodeId, fieldName: content.field_name });
      setWaitingFor('user_input');
      return;
    }

    // ── ai_handoff ─────────────────────────────────────────────────────
    if (type === 'ai_handoff') {
      const text = engine.substituteVariables(content.text || '', vars);
      if (text) pushHistory({ type: 'bot', text, nodeType: type, nodeId });
      pushHistory({
        type: 'system',
        text: '⚡ Handed off to AI assistant — live AI responses are not simulated in preview.',
        severity: 'info',
      });
      const next = engine.resolveNext(nodeId, 'default');
      if (next) { await stepEngine(engine, next, vars); }
      else { setPhase('terminal'); }
      return;
    }

    // ── api_call ───────────────────────────────────────────────────────
    if (type === 'api_call') {
      const resolvedContent = substituteApiCallContent(engine, content, vars);
      pushHistory({ type: 'api_loading', nodeId, url: resolvedContent.url, method: resolvedContent.method });

      let success = false;
      let responseData = null;
      let errorMessage = '';

      try {
        if (typeof onPreviewApiCall !== 'function') throw new Error('Preview API call handler not configured.');
        const res = await onPreviewApiCall(resolvedContent);
        const resData = res?.data;
        const body = resData?.data?.responseBody ?? resData?.data ?? resData;
        responseData = body;
        const statusCode = resData?.data?.statusCode ?? resData?.data?.status ?? 200;
        success = Number(statusCode) < 400;
      } catch (err) {
        errorMessage = err?.response?.data?.message || err?.message || 'Request failed';
        success = false;
      }

      setHistory((prev) => {
        const next = prev.filter((h) => !(h.type === 'api_loading' && h.nodeId === nodeId));
        return [
          ...next,
          {
            type: 'api_result', nodeId, success,
            url: resolvedContent.url, method: resolvedContent.method,
            errorMessage: success ? '' : errorMessage,
            extractedVars: success ? engine.applyResponseMap(content.response_map || {}, responseData) : {},
          },
        ];
      });

      if (success) {
        const extracted = engine.applyResponseMap(content.response_map || {}, responseData);
        const newVars = { ...vars, ...extracted };
        variablesRef.current = newVars;
        setVariables(newVars);

        const next = engine.resolveNext(nodeId, 'success');
        if (!next && content.success_text) {
          pushHistory({ type: 'bot', text: engine.substituteVariables(content.success_text, newVars), nodeType: 'message', nodeId: null });
        }
        if (next) { await stepEngine(engine, next, newVars); }
        else if (!content.success_text) { setPhase('terminal'); }
      } else {
        if (content.error_text) {
          pushHistory({ type: 'bot', text: engine.substituteVariables(content.error_text, vars), nodeType: 'message', nodeId: null });
        }
        const next = engine.resolveNext(nodeId, 'error');
        if (next) { await stepEngine(engine, next, vars); }
        else { setPhase('terminal'); }
      }
      return;
    }

    // ── end ────────────────────────────────────────────────────────────
    if (type === 'end') {
      const text = engine.substituteVariables(content.text || '', vars);
      if (text) pushHistory({ type: 'bot', text, nodeType: type, nodeId });
      pushHistory({ type: 'system', text: '✅ Flow completed.', severity: 'success' });
      setWaitingFor(null);
      setPhase('terminal');
      return;
    }

    pushHistory({ type: 'system', text: `Unknown node type "${type}" — preview stopped.`, severity: 'error' });
    setPhase('terminal');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onPreviewApiCall, pushHistory]);

  // ── Start / Restart ───────────────────────────────────────────────────
  const startPreview = useCallback(() => {
    const engine = new FlowPreviewEngine(nodes, edges);
    engineRef.current = engine;
    const startNode = engine.getStartNode();

    variablesRef.current = {};
    stepCountRef.current = 0;

    setPhase('running');
    setHistory([]);
    setVariables({});
    setWaitingFor(null);
    setInputText('');
    setCurrentNodeId(null);
    setPath([]);
    setStepCount(0);

    if (!startNode) {
      setHistory([{ type: 'system', text: 'No start node found.', severity: 'error' }]);
      setPhase('terminal');
      return;
    }
    stepEngine(engine, startNode.id, {});
  }, [nodes, edges, stepEngine]);

  // ── Handle user choice ────────────────────────────────────────────────
  const handleChoice = useCallback((handleId, label, botText) => {
    if (waitingFor !== 'user_choice') return;
    const engine = engineRef.current;
    if (!engine) return;
    setWaitingFor(null);
    pushHistory({ type: 'user', text: label, quotedText: botText || '', quotedFrom: companyName });
    const next = engine.resolveNext(currentNodeId, handleId);
    stepEngine(engine, next, variablesRef.current);
  }, [waitingFor, currentNodeId, stepEngine, pushHistory, companyName]);

  // ── Handle user input ─────────────────────────────────────────────────
  const handleInputSubmit = useCallback(() => {
    if (waitingFor !== 'user_input') return;
    const engine = engineRef.current;
    if (!engine || !inputText.trim()) return;

    const node = engine.getNode(currentNodeId);
    const fieldName = node?.data?.content?.field_name || '__input__';
    const text = inputText.trim();

    setWaitingFor(null);
    setInputText('');
    pushHistory({ type: 'user', text });

    const newVars = { ...variablesRef.current, [fieldName]: text };
    variablesRef.current = newVars;
    setVariables(newVars);

    const next = engine.resolveNext(currentNodeId, 'default');
    stepEngine(engine, next, newVars);
  }, [waitingFor, inputText, currentNodeId, stepEngine, pushHistory]);

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleInputSubmit();
    }
  };

  useEffect(() => {
    if (waitingFor === 'user_input') setTimeout(() => inputRef.current?.focus(), 50);
  }, [waitingFor]);

  const canStart = validation.errors.length === 0 &&
    (!hasApiCallNodes || apiDisclaimerAcknowledged);

  const showApiDisclaimer = hasApiCallNodes && !apiDisclaimerAcknowledged &&
    validation.errors.length === 0;

  // ─── Render ────────────────────────────────────────────────────────────
  return (
    <div className="fpp-panel">

      {/* ── WhatsApp-style header ─────────────────────────────────── */}
      <div className="fpp-wa-header">
        <div className="fpp-wa-left">
          <div className="fpp-wa-avatar">
            <svg width="32" height="32" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="20" cy="20" r="20" fill="#25D366"/>
              <path d="M20 10C17.79 10 16 11.79 16 14C16 16.21 17.79 18 20 18C22.21 18 24 16.21 24 14C24 11.79 22.21 10 20 10ZM20 20C16.67 20 10 21.67 10 25V28H30V25C30 21.67 23.33 20 20 20Z" fill="white"/>
            </svg>
          </div>
          <div className="fpp-wa-info">
            <div className="fpp-wa-name">{companyName}</div>
            <div className="fpp-wa-status">
              {phase === 'running' ? 'Simulating…' : phase === 'terminal' ? 'Ended' : 'Flow Preview'}
            </div>
          </div>
        </div>
        <div className="fpp-wa-actions">
          {phase !== 'idle' && (
            <button className="fpp-btn-restart" onClick={startPreview} title="Restart preview">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-4.5"/>
              </svg>
            </button>
          )}
          <button className="fpp-btn-exit" onClick={onClose} title="Exit preview">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      </div>

      {/* ── Compact banners ───────────────────────────────────────── */}
      <div className="fpp-banners">
        {hasDraftChanges && (
          <div className="fpp-banner fpp-banner--draft">
            ⚠ Preview reflects last <strong>saved</strong> state — unsaved edits not included.
          </div>
        )}

        {validation.errors.length > 0 && (
          <div className="fpp-banner fpp-banner--error">
            <strong>Cannot preview:</strong>{' '}
            {validation.errors[0].message}
            {validation.errors.length > 1 && ` (+${validation.errors.length - 1} more)`}
          </div>
        )}

        {validation.warnings.length > 0 && !warningsDismissed && validation.errors.length === 0 && (
          <div className="fpp-banner fpp-banner--warning">
            <span>⚠ {validation.warnings.length} warning{validation.warnings.length > 1 ? 's' : ''} — {validation.warnings[0].message}</span>
            <button className="fpp-banner-dismiss" onClick={() => setWarningsDismissed(true)}>✕</button>
          </div>
        )}

        {showApiDisclaimer && (
          <div className="fpp-banner fpp-banner--api">
            <span>⚡ This flow has <strong>API Call</strong> nodes. Preview will make real HTTP requests. No WhatsApp messages will be sent.</span>
            <button className="fpp-banner-confirm" onClick={() => setApiDisclaimerAcknowledged(true)}>Got it</button>
          </div>
        )}
      </div>

      {/* ── Chat area (WhatsApp wallpaper background) ─────────────── */}
      {phase === 'idle' ? (
        <div className="fpp-idle">
          <div className="fpp-idle-icon">💬</div>
          <div className="fpp-idle-title">Simulate this flow</div>
          <div className="fpp-idle-desc">
            Step through the conversation exactly as your users would.
            Branch choices, inputs, and API calls all execute.
          </div>
          <button
            className="fpp-btn-start"
            onClick={startPreview}
            disabled={!canStart}
          >
            ▶&nbsp;Start Preview
          </button>
        </div>
      ) : (
        <div className="fpp-chat-area">
          {(() => {
            // Find the index of the last bot message with options (for active button state)
            let lastBotWithOptionsIdx = -1;
            if (waitingFor === 'user_choice') {
              for (let i = history.length - 1; i >= 0; i--) {
                if (history[i].type === 'bot' && history[i].options?.length > 0) {
                  lastBotWithOptionsIdx = i;
                  break;
                }
              }
            }
            return history.map((item, idx) => (
              <ChatBubble
                key={idx}
                item={item}
                isLastWithChoices={idx === lastBotWithOptionsIdx}
                onChoice={handleChoice}
              />
            ));
          })()}
          <div ref={chatEndRef} />
        </div>
      )}

      {/* ── WhatsApp-style input bar ──────────────────────────────── */}
      {phase === 'running' && waitingFor === 'user_input' && (
        <div className="fpp-input-row">
          <input
            ref={inputRef}
            className="fpp-input-field"
            type="text"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={handleInputKeyDown}
            placeholder="Type a message…"
          />
          <button
            className="fpp-send-btn"
            onClick={handleInputSubmit}
            disabled={!inputText.trim()}
            title="Send"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22 2L11 13" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      )}

      {/* Placeholder bar when not waiting for input (keeps layout stable) */}
      {(phase === 'running' && waitingFor !== 'user_input') || phase === 'terminal' ? (
        <div className="fpp-input-row fpp-input-row--inactive">
          <input
            className="fpp-input-field"
            type="text"
            disabled
            placeholder={phase === 'terminal' ? 'Preview ended' : 'Waiting for bot…'}
          />
          <button className="fpp-send-btn fpp-send-btn--disabled" disabled title="Send">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22 2L11 13" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default FlowPreviewPanel;
